"use client";

import { X, Briefcase, User, Calendar, FileText, CheckCircle2, ShieldCheck, Mail, Phone, MapPin, Award, ExternalLink } from "lucide-react";
import { CandidateLead } from "@/lib/demos/hiringDb";

interface CandidateDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidate: CandidateLead | null;
  onOpenStageModal?: (candidate: CandidateLead, targetStage: string) => void;
}

export function CandidateDetailModal({
  isOpen,
  onClose,
  candidate,
  onOpenStageModal,
}: CandidateDetailModalProps) {
  if (!isOpen || !candidate) return null;

  const sym = candidate.currency === "USD" ? "$" : "₹";

  let skills: string[] = [];
  try {
    skills = JSON.parse(candidate.skills_tags || "[]");
  } catch (e) {
    skills = ["Executive Leadership", "Org Strategy", "P&L Management"];
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-2xl bg-[#140c21] border border-purple-800/50 rounded-2xl shadow-2xl overflow-hidden text-white">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-purple-900/40 bg-[#170f2b]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm shadow-md shadow-purple-500/20">
              {candidate.candidate_name
                .split(" ")
                .map((n) => n[0])
                .slice(0, 2)
                .join("")}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-extrabold text-white text-base">
                  {candidate.candidate_name}
                </h3>
                <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded bg-purple-500/10 text-purple-300 border border-purple-500/20">
                  {candidate.candidate_no}
                </span>
              </div>
              <p className="text-xs text-purple-300 font-medium">
                {candidate.target_role} • {candidate.client_company}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-purple-900/40 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 max-h-[80vh] overflow-y-auto space-y-5 text-xs">
          {/* Top Status & Practice Banner */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3.5 rounded-xl bg-[#19102a] border border-purple-900/40">
            <div>
              <div className="text-[10px] text-slate-500">Pipeline Stage</div>
              <div className="font-bold text-purple-300 text-xs mt-0.5 truncate">{candidate.stage}</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-500">Practice Area</div>
              <div className="font-bold text-white text-xs mt-0.5 truncate">{candidate.practice_name}</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-500">Experience</div>
              <div className="font-bold text-emerald-400 text-xs mt-0.5">{candidate.years_experience} Years</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-500">Vetting Score</div>
              <div className="font-bold text-amber-400 text-xs mt-0.5 flex items-center gap-1">
                <Award className="w-3.5 h-3.5" />
                <span>{candidate.evaluation_score} / 100</span>
              </div>
            </div>
          </div>

          {/* Current Bio & Contact Information */}
          <div className="p-4 rounded-xl bg-[#170e26] border border-purple-900/40 space-y-2.5">
            <div className="text-[11px] font-bold text-purple-300 uppercase tracking-wider">
              Executive Bio &amp; Current Mandate
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-slate-300">
              <div className="flex items-center gap-2">
                <Briefcase className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                <span className="truncate">Current: <strong>{candidate.current_title}</strong> ({candidate.current_company})</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                <span>{candidate.location}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                <span className="font-mono text-purple-300 truncate">{candidate.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                <span className="font-mono">{candidate.phone || "Direct Line Restricted"}</span>
              </div>
            </div>
          </div>

          {/* Compensation & Fee Structure */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 rounded-xl bg-[#10091c] border border-purple-900/40">
            <div>
              <div className="text-[10px] text-slate-500 uppercase">Target Annual CTC</div>
              <div className="text-sm font-extrabold text-white mt-0.5">
                {sym}{candidate.expected_ctc.toLocaleString()}
              </div>
            </div>
            <div>
              <div className="text-[10px] text-slate-500 uppercase">Offered CTC</div>
              <div className="text-sm font-extrabold text-emerald-400 mt-0.5">
                {candidate.offer_ctc ? `${sym}${candidate.offer_ctc.toLocaleString()}` : "In Review"}
              </div>
            </div>
            <div>
              <div className="text-[10px] text-slate-500 uppercase">Success Placement Fee</div>
              <div className="text-sm font-extrabold text-purple-300 mt-0.5">
                {sym}{candidate.placement_fee.toLocaleString()}
              </div>
            </div>
          </div>

          {/* Competency & Skills Badges */}
          <div className="space-y-2">
            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              Vetted Competencies &amp; Architectural Acuity
            </div>
            <div className="flex flex-wrap gap-1.5">
              {skills.map((skill, idx) => (
                <span
                  key={idx}
                  className="px-2.5 py-1 rounded-lg bg-purple-500/10 text-purple-300 border border-purple-500/20 text-[11px] font-medium"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>

          {/* Interview Details (If scheduled) */}
          {candidate.interview_date && (
            <div className="p-4 rounded-xl bg-[#1b112f] border border-purple-800/40 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-purple-300 font-bold text-xs">
                  <Calendar className="w-4 h-4 text-purple-400" />
                  <span>Scheduled Interview Panel Itinerary</span>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded bg-purple-500/20 text-purple-200 border border-purple-500/30">
                  Briefing PDF Dispatched
                </span>
              </div>
              <div className="text-slate-300 text-xs space-y-1">
                <div>Slot: <strong className="text-white">{candidate.interview_date}</strong></div>
                <div>Panel: <strong className="text-white">{candidate.interviewer_name}</strong></div>
                {candidate.meeting_link && (
                  <div className="flex items-center gap-1.5 pt-1">
                    <span className="text-slate-400">Meeting URL:</span>
                    <a
                      href={candidate.meeting_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:underline flex items-center gap-1 truncate"
                    >
                      <span>{candidate.meeting_link}</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Placement Won Invoice Reference (If Won) */}
          {candidate.stage === "Offer Released & Placed (Won)" && candidate.invoice_no && (
            <div className="p-4 rounded-xl bg-emerald-950/30 border border-emerald-500/30 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-emerald-300 font-bold text-xs">
                  <FileText className="w-4 h-4 text-emerald-400" />
                  <span>Executed Agreement &amp; Tax Invoice</span>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                  Active Warranty
                </span>
              </div>
              <div className="text-slate-300 text-xs space-y-1">
                <div>Tax Invoice No: <strong className="text-white font-mono">{candidate.invoice_no}</strong></div>
                <div>Engagement Ref: <strong className="text-white font-mono">{candidate.agreement_id}</strong></div>
                <div className="text-emerald-400 font-semibold">Warranty Terms: 90/180-Day Executive Replacement Guarantee Active</div>
              </div>
            </div>
          )}

          {/* Recruiter Evaluation Notes */}
          <div className="p-3.5 rounded-xl bg-[#150d24] border border-purple-900/40 space-y-1">
            <div className="text-[10px] font-bold text-slate-400 uppercase">
              Lead Search Partner Assessment ({candidate.assigned_recruiter})
            </div>
            <p className="text-slate-300 text-xs leading-relaxed italic">
              &ldquo;{candidate.notes || "High-caliber executive candidate with audited track record."}&rdquo;
            </p>
          </div>

          {/* Footer Action Buttons */}
          <div className="pt-2 flex items-center justify-between">
            <div className="text-[11px] text-slate-500">
              Created: {new Date(candidate.created_at).toLocaleDateString()}
            </div>

            <div className="flex gap-2">
              {onOpenStageModal && candidate.stage !== "Offer Released & Placed (Won)" && (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onOpenStageModal(
                      candidate,
                      candidate.stage === "Client Interview Round"
                        ? "Offer Released & Placed (Won)"
                        : "Client Interview Round"
                    );
                  }}
                  className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-semibold text-xs transition-colors"
                >
                  {candidate.stage === "Client Interview Round"
                    ? "Advance to Placed & Invoice"
                    : "Schedule Interview Round"}
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-xl bg-purple-950/60 hover:bg-purple-900 text-slate-300 hover:text-white border border-purple-800/40 text-xs font-semibold"
              >
                Close Dossier
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
